/**
 * HOME_Insurance_Wizard_v06 (React + TypeScript + React-Bootstrap)
 * Version: v06-autosave + themes
 * Date: 2025-09-06 (America/Denver)
 *
 * New:
 * - Three CSS themes: "classic", "midnight", "ocean"
 * - Easy switching via the `theme` prop or an optional built-in dropdown (`showThemeSwitcher`)
 * - Preserves autosave: creates after Step 1, updates thereafter
 */

import React, {
  useMemo,
  useState,
  useCallback,
  PropsWithChildren,
  useEffect,
  useRef,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Carousel,
  Col,
  Container,
  Form,
  InputGroup,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";

/* ============================================================
   Declarations (tunable variables & types)
   ============================================================ */
const VERSION = "v06-autosave+themes";
const SHOW_VERSION = true; // toggle version badge on/off
const TEST_MODE = true; // true = simulate API; false = POST to API Gateway
const API_GATEWAY_URL =
  "https://your-api-id.execute-api.us-east-1.amazonaws.com/prod/submit"; // <-- replace

const SLIDE_ANIMATION_MS = 450; // carousel slide duration
const SHOW_PROGRESS_BAR = true;

// --- Help text effect controls ---
export type HelpEffect = "none" | "typewriter" | "fade";
/** Choose the global help text effect (runs once per slide) */
const HELP_EFFECT: HelpEffect = "typewriter"; // "none" | "typewriter" | "fade"
const TYPEWRITER_SPEED_MS = 20;
const TYPEWRITER_CURSOR = true;
const FADE_DURATION_MS = 400;

// --- Themes ---
export type ThemeName = "classic" | "midnight" | "ocean";

const LABELS = {
  title: "Homeowners Insurance Wizard",
  subtitle: "Tell us about you, your property, and coverage preferences",
  stepTitles: ["Contact", "Property", "Occupancy & History", "Coverage", "Summary"],
  buttons: {
    back: "Back",
    next: "Next",
    finish: "Finish",
    edit: "Edit",
    submit: "Submit",
  },
  notices: {
    required: "Required fields are marked with *",
  },
} as const;

const STEP_HELP = [
  {
    title: "Why we need your property address",
    text:
      "Rates depend on where the home is located (fire protection, weather, crime). Accurate contact & address ensures the right pricing and forms.",
  },
  {
    title: "Property details matter",
    text:
      "Year built, construction, roof type and updates affect replacement cost and eligibility.",
  },
  {
    title: "Occupancy & history",
    text:
      "Primary vs. secondary vs. rental, time occupied, and prior claims help carriers underwrite risk.",
  },
  {
    title: "Coverage preferences",
    text:
      "Choose limits and deductibles that meet your lender’s requirements and your risk tolerance.",
  },
] as const;

/* ============================================================
   Types & defaults
   ============================================================ */
export type WizardData = {
  // Contact & Address
  fullName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string; // 2-letter
  zip: string; // 5 or 9

  // Property
  dwellingType: string; // Single Family, Condo, Townhome, Multi-Family (2-4), Manufactured/Mobile
  yearBuilt: string;
  squareFeet: string;
  stories: string;
  constructionType: string; // Frame, Masonry, Masonry Veneer, Other
  roofType: string; // Asphalt, Metal, Tile, Flat/Other
  roofYear: string;
  heatingType: string; // Forced Air, Electric Baseboard, Heat Pump, Boiler, Other

  // Occupancy & History
  occupancy: string; // Primary, Secondary, Seasonal, Rental
  monthsOccupied: string; // 0-12
  ownershipStatus: string; // Owned Outright | Financed
  mortgageeName: string; // required if Financed
  alarmMonitored: string; // optional Yes/No
  priorClaims5y: string; // integer

  // Coverage
  coverageA_Dwelling: string; // number
  coverageC_PersonalProperty: string; // number
  liabilityLimit: string; // required
  medicalPayments: string; // optional
  deductible: string; // required
  ordinanceOrLaw: string; // optional Yes/No
  replacementCostContents: string; // optional Yes/No
  waterBackup: string; // optional Yes/No
};

const defaultData: WizardData = {
  // Contact & Address
  fullName: "",
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zip: "",

  // Property
  dwellingType: "",
  yearBuilt: "",
  squareFeet: "",
  stories: "",
  constructionType: "",
  roofType: "",
  roofYear: "",
  heatingType: "",

  // Occupancy & History
  occupancy: "",
  monthsOccupied: "",
  ownershipStatus: "",
  mortgageeName: "",
  alarmMonitored: "",
  priorClaims5y: "",

  // Coverage
  coverageA_Dwelling: "",
  coverageC_PersonalProperty: "",
  liabilityLimit: "",
  medicalPayments: "",
  deductible: "",
  ordinanceOrLaw: "",
  replacementCostContents: "",
  waterBackup: "",
};

/* ============================================================
   Effects (typewriter/fade)
   ============================================================ */
const TypewriterText: React.FC<{
  text: string;
  speedMs?: number;
  showCursor?: boolean;
  triggerKey?: string | number;
  className?: string;
}> = ({
  text,
  speedMs = TYPEWRITER_SPEED_MS,
  showCursor = TYPEWRITER_CURSOR,
  triggerKey,
  className,
}) => {
  const [idx, setIdx] = useState<number>(0);
  const tRef = useRef<number | null>(null);

  useEffect(() => {
    setIdx(0);
  }, [triggerKey, text]);

  useEffect(() => {
    if (idx >= text.length) return;
    tRef.current = window.setTimeout(() => setIdx((i) => i + 1), speedMs);
    return () => {
      if (tRef.current) window.clearTimeout(tRef.current);
    };
  }, [idx, text.length, speedMs]);

  const done = idx >= text.length;
  return (
    <span className={className}>
      {text.slice(0, idx)}
      {showCursor && !done ? <span className="tw-cursor">|</span> : null}
    </span>
  );
};

const FadeText: React.FC<{
  text: string;
  durationMs?: number;
  triggerKey?: string | number;
  className?: string;
}> = ({ text, durationMs = FADE_DURATION_MS, triggerKey, className }) => {
  const [visible, setVisible] = useState<boolean>(false);
  useEffect(() => {
    setVisible(false);
    const t = window.setTimeout(() => setVisible(true), 10);
    return () => window.clearTimeout(t);
  }, [triggerKey, text]);

  return (
    <span
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${durationMs}ms ease`,
        display: "inline-block",
      }}
    >
      {text}
    </span>
  );
};

/* ============================================================
   UI helpers
   ============================================================ */
export type HelpPanelProps = {
  title: string;
  text: string;
  className?: string;
  effect?: HelpEffect;
  triggerKey?: string | number;
};
const HelpPanel: React.FC<HelpPanelProps> = ({
  title,
  text,
  className,
  effect = "none",
  triggerKey,
}) => (
  <Alert variant="info" className={`mb-3 ${className ?? ""}`.trim()}>
    <div className="fw-semibold mb-1">{title}</div>
    <div className="small text-muted">
      {effect === "typewriter" ? (
        <TypewriterText text={text} triggerKey={triggerKey} />
      ) : effect === "fade" ? (
        <FadeText text={text} triggerKey={triggerKey} />
      ) : (
        text
      )}
    </div>
  </Alert>
);

export type FieldNoteProps = PropsWithChildren<{ className?: string }>;
const FieldNote: React.FC<FieldNoteProps> = ({ children, className }) => (
  <div className={`form-text ${className ?? ""}`.trim()}>{children}</div>
);

/** Utility validations */
const isNumeric = (s: string) => /^\d+(\.\d+)?$/.test(s.trim());
const isInt = (s: string) => /^\d+$/.test(s.trim());
const isEmail = (s: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.trim());
const digitsOnly = (s: string) => s.replace(/\D/g, "");
const isZip = (s: string) => /^\d{5}(?:-\d{4})?$/.test(s.trim());
const isState2 = (s: string) => /^[A-Za-z]{2}$/.test(s.trim());
const isYear = (s: string) => {
  const y = parseInt(s, 10);
  const now = new Date().getFullYear();
  return Number.isFinite(y) && y >= 1850 && y <= now + 1;
};
const isMonth0to12 = (s: string) => /^(?:0?[0-9]|1[0-2])$/.test(s.trim());

/* ============================================================
   Validation map (field-wise)
   ============================================================ */
export type ValidMap = Record<keyof WizardData, boolean>;
export type ErrorItem = { field: keyof WizardData; label: string; reason: string };

const FIELD_LABEL: Record<keyof WizardData, string> = {
  // Contact
  fullName: "Full Name",
  email: "Email",
  phone: "Phone",
  street: "Street",
  city: "City",
  state: "State",
  zip: "ZIP",

  // Property
  dwellingType: "Dwelling Type",
  yearBuilt: "Year Built",
  squareFeet: "Square Feet",
  stories: "Stories",
  constructionType: "Construction Type",
  roofType: "Roof Type",
  roofYear: "Roof Year",
  heatingType: "Heating Type",

  // Occupancy & History
  occupancy: "Occupancy",
  monthsOccupied: "Months Occupied",
  ownershipStatus: "Ownership Status",
  mortgageeName: "Mortgagee Name",
  alarmMonitored: "Monitored Alarm",
  priorClaims5y: "Prior Claims (5 yrs)",

  // Coverage
  coverageA_Dwelling: "Coverage A (Dwelling)",
  coverageC_PersonalProperty: "Coverage C (Personal Property)",
  liabilityLimit: "Liability Limit",
  medicalPayments: "Medical Payments",
  deductible: "Deductible",
  ordinanceOrLaw: "Ordinance or Law",
  replacementCostContents: "Replacement Cost on Contents",
  waterBackup: "Water Backup",
};

function buildValidity(data: WizardData): {
  valid: ValidMap;
  errors: ErrorItem[];
} {
  const valid: ValidMap = {
    // Contact
    fullName: data.fullName.trim().length > 1,
    email: isEmail(data.email),
    phone: digitsOnly(data.phone).length >= 10,
    street: data.street.trim().length > 3,
    city: data.city.trim().length > 1,
    state: isState2(data.state),
    zip: isZip(data.zip),

    // Property
    dwellingType: !!data.dwellingType,
    yearBuilt: isYear(data.yearBuilt),
    squareFeet: isNumeric(data.squareFeet) && parseFloat(data.squareFeet) > 200,
    stories: isInt(data.stories) && parseInt(data.stories, 10) > 0,
    constructionType: !!data.constructionType,
    roofType: !!data.roofType,
    roofYear: isYear(data.roofYear),
    heatingType: !!data.heatingType,

    // Occupancy & History
    occupancy: !!data.occupancy,
    monthsOccupied: isMonth0to12(data.monthsOccupied),
    ownershipStatus: !!data.ownershipStatus,
    mortgageeName: data.ownershipStatus === "Financed" ? data.mortgageeName.trim().length > 1 : true,
    alarmMonitored: true, // optional
    priorClaims5y: isInt(data.priorClaims5y || "0"),

    // Coverage
    coverageA_Dwelling: isNumeric(data.coverageA_Dwelling) && parseFloat(data.coverageA_Dwelling) > 0,
    coverageC_PersonalProperty: isNumeric(data.coverageC_PersonalProperty) && parseFloat(data.coverageC_PersonalProperty) > 0,
    liabilityLimit: !!data.liabilityLimit,
    medicalPayments: true, // optional
    deductible: !!data.deductible,
    ordinanceOrLaw: true, // optional
    replacementCostContents: true, // optional
    waterBackup: true, // optional
  };

  const errors: ErrorItem[] = [];
  (Object.keys(valid) as (keyof WizardData)[]).forEach((k) => {
    if (!valid[k]) {
      let reason = "missing or invalid";
      if (k === "email") reason = "invalid email";
      if (k === "phone") reason = "phone too short";
      if (k === "state") reason = "use 2-letter code (e.g., CO)";
      if (k === "zip") reason = "5 digits (or ZIP+4)";
      if (k === "yearBuilt" || k === "roofYear") reason = "enter a 4-digit year";
      if (k === "squareFeet") reason = "must be a number > 200";
      if (k === "stories") reason = "must be a whole number";
      if (k === "monthsOccupied") reason = "enter 0–12";
      if (k === "priorClaims5y") reason = "enter 0 if none";
      if (k === "coverageA_Dwelling" || k === "coverageC_PersonalProperty") reason = "must be a dollar amount";
      if (k === "liabilityLimit" || k === "deductible") reason = "required";
      if (k === "mortgageeName") reason = "required when financed";
      errors.push({ field: k, label: FIELD_LABEL[k], reason });
    }
  });

  return { valid, errors };
}

/* ============================================================
   Validated Control wrapper (adds ✓ when valid)
   ============================================================ */
const ValidWrap: React.FC<{ valid: boolean; children: React.ReactNode }> = ({
  valid,
  children,
}) => (
  <InputGroup>
    {children}
    {valid && <InputGroup.Text aria-label="valid">✓</InputGroup.Text>}
  </InputGroup>
);

/* ============================================================
   Step subcomponents (help effect runs once per slide)
   ============================================================ */
export type StepProps = {
  data: WizardData;
  setData: (partial: Partial<WizardData>) => void;
  helpEffect: HelpEffect;
  helpKey: string | number;
  valid: ValidMap;
};

const Step1Contact: React.FC<StepProps> = ({
  data,
  setData,
  helpEffect,
  helpKey,
  valid,
}) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[0].title}
        text={STEP_HELP[0].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Form.Group className="mb-3">
        <Form.Label>Full Name *</Form.Label>
        <ValidWrap valid={valid.fullName}>
          <Form.Control
            value={data.fullName}
            onChange={(e) => setData({ fullName: e.target.value })}
            placeholder="Jane Doe"
            required
            className={valid.fullName ? "is-valid" : data.fullName ? "is-invalid" : ""}
          />
        </ValidWrap>
        <FieldNote>We’ll reference this on your quote and policy docs.</FieldNote>
      </Form.Group>

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Email *</Form.Label>
            <ValidWrap valid={valid.email}>
              <Form.Control
                type="email"
                value={data.email}
                onChange={(e) => setData({ email: e.target.value })}
                placeholder="name@example.com"
                required
                className={valid.email ? "is-valid" : data.email ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>Used for sending quotes and policy updates.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Phone *</Form.Label>
            <ValidWrap valid={valid.phone}>
              <Form.Control
                value={data.phone}
                onChange={(e) => setData({ phone: e.target.value })}
                placeholder="(555) 555-5555"
                required
                className={valid.phone ? "is-valid" : data.phone ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>We’ll call/text for time-sensitive items.</FieldNote>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Street Address *</Form.Label>
            <ValidWrap valid={valid.street}>
              <Form.Control
                value={data.street}
                onChange={(e) => setData({ street: e.target.value })}
                placeholder="123 Main St"
                required
                className={valid.street ? "is-valid" : data.street ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>City *</Form.Label>
            <ValidWrap valid={valid.city}>
              <Form.Control
                value={data.city}
                onChange={(e) => setData({ city: e.target.value })}
                placeholder="Denver"
                required
                className={valid.city ? "is-valid" : data.city ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>State *</Form.Label>
            <ValidWrap valid={valid.state}>
              <Form.Control
                value={data.state}
                onChange={(e) => setData({ state: e.target.value.toUpperCase() })}
                placeholder="CO"
                required
                className={valid.state ? "is-valid" : data.state ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>ZIP *</Form.Label>
            <ValidWrap valid={valid.zip}>
              <Form.Control
                value={data.zip}
                onChange={(e) => setData({ zip: e.target.value })}
                placeholder="80202"
                required
                className={valid.zip ? "is-valid" : data.zip ? "is-invalid" : ""}
              />
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step2Property: React.FC<StepProps> = ({
  data,
  setData,
  helpEffect,
  helpKey,
  valid,
}) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[1].title}
        text={STEP_HELP[1].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Dwelling Type *</Form.Label>
            <ValidWrap valid={valid.dwellingType}>
              <Form.Select
                value={data.dwellingType}
                onChange={(e) => setData({ dwellingType: e.target.value })}
                required
                className={
                  valid.dwellingType ? "is-valid" : data.dwellingType ? "is-invalid" : ""
                }
              >
                <option value="">Select…</option>
                <option>Single Family</option>
                <option>Condo</option>
                <option>Townhome</option>
                <option>Multi-Family (2-4)</option>
                <option>Manufactured/Mobile</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Year Built *</Form.Label>
            <ValidWrap valid={valid.yearBuilt}>
              <Form.Control
                value={data.yearBuilt}
                onChange={(e) => setData({ yearBuilt: e.target.value })}
                placeholder="1998"
                required
                className={
                  valid.yearBuilt ? "is-valid" : data.yearBuilt ? "is-invalid" : ""
                }
              />
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Stories *</Form.Label>
            <ValidWrap valid={valid.stories}>
              <Form.Control
                value={data.stories}
                onChange={(e) => setData({ stories: e.target.value })}
                placeholder="1"
                required
                className={valid.stories ? "is-valid" : data.stories ? "is-invalid" : ""}
              />
              <InputGroup.Text>stories</InputGroup.Text>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Square Feet *</Form.Label>
            <ValidWrap valid={valid.squareFeet}>
              <Form.Control
                value={data.squareFeet}
                onChange={(e) => setData({ squareFeet: e.target.value })}
                placeholder="2200"
                required
                className={
                  valid.squareFeet ? "is-valid" : data.squareFeet ? "is-invalid" : ""
                }
              />
              <InputGroup.Text>sq ft</InputGroup.Text>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Construction Type *</Form.Label>
            <ValidWrap valid={valid.constructionType}>
              <Form.Select
                value={data.constructionType}
                onChange={(e) => setData({ constructionType: e.target.value })}
                required
                className={
                  valid.constructionType
                    ? "is-valid"
                    : data.constructionType
                    ? "is-invalid"
                    : ""
                }
              >
                <option value="">Select…</option>
                <option>Frame</option>
                <option>Masonry</option>
                <option>Masonry Veneer</option>
                <option>Other</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Heating Type *</Form.Label>
            <ValidWrap valid={valid.heatingType}>
              <Form.Select
                value={data.heatingType}
                onChange={(e) => setData({ heatingType: e.target.value })}
                required
                className={
                  valid.heatingType ? "is-valid" : data.heatingType ? "is-invalid" : ""
                }
              >
                <option value="">Select…</option>
                <option>Forced Air</option>
                <option>Electric Baseboard</option>
                <option>Heat Pump</option>
                <option>Boiler/Radiator</option>
                <option>Wood/Pellet</option>
                <option>Other</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Roof Type *</Form.Label>
            <ValidWrap valid={valid.roofType}>
              <Form.Select
                value={data.roofType}
                onChange={(e) => setData({ roofType: e.target.value })}
                required
                className={valid.roofType ? "is-valid" : data.roofType ? "is-invalid" : ""}
              >
                <option value="">Select…</option>
                <option>Asphalt Shingle</option>
                <option>Metal</option>
                <option>Tile/Slate</option>
                <option>Flat/Other</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Roof Year *</Form.Label>
            <ValidWrap valid={valid.roofYear}>
              <Form.Control
                value={data.roofYear}
                onChange={(e) => setData({ roofYear: e.target.value })}
                placeholder="2018"
                required
                className={valid.roofYear ? "is-valid" : data.roofYear ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>Approximate is fine if unsure.</FieldNote>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step3OccupancyHistory: React.FC<StepProps> = ({
  data,
  setData,
  helpEffect,
  helpKey,
  valid,
}) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[2].title}
        text={STEP_HELP[2].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Row className="g-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Occupancy *</Form.Label>
            <ValidWrap valid={valid.occupancy}>
              <Form.Select
                value={data.occupancy}
                onChange={(e) => setData({ occupancy: e.target.value })}
                required
                className={valid.occupancy ? "is-valid" : data.occupancy ? "is-invalid" : ""}
              >
                <option value="">Select…</option>
                <option>Primary</option>
                <option>Secondary</option>
                <option>Seasonal</option>
                <option>Rental</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Months Occupied (per year) *</Form.Label>
            <ValidWrap valid={valid.monthsOccupied}>
              <Form.Control
                value={data.monthsOccupied}
                onChange={(e) => setData({ monthsOccupied: e.target.value })}
                placeholder="12"
                required
                className={
                  valid.monthsOccupied ? "is-valid" : data.monthsOccupied ? "is-invalid" : ""
                }
              />
              <InputGroup.Text>months</InputGroup.Text>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Ownership Status *</Form.Label>
            <ValidWrap valid={valid.ownershipStatus}>
              <Form.Select
                value={data.ownershipStatus}
                onChange={(e) => setData({ ownershipStatus: e.target.value })}
                required
                className={
                  valid.ownershipStatus ? "is-valid" : data.ownershipStatus ? "is-invalid" : ""
                }
              >
                <option value="">Select…</option>
                <option>Owned Outright</option>
                <option>Financed</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={8}>
          <Form.Group>
            <Form.Label>
              Mortgagee Name {data.ownershipStatus === "Financed" ? "*" : "(if financed)"}
            </Form.Label>
            <ValidWrap valid={valid.mortgageeName}>
              <Form.Control
                value={data.mortgageeName}
                onChange={(e) => setData({ mortgageeName: e.target.value })}
                placeholder="Bank / Lender Name"
                className={
                  valid.mortgageeName ? "is-valid" : data.mortgageeName ? "is-invalid" : ""
                }
                required={data.ownershipStatus === "Financed"}
              />
            </ValidWrap>
            <FieldNote>Needed to list your lender as mortgagee if applicable.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Monitored Alarm</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.alarmMonitored}
                onChange={(e) => setData({ alarmMonitored: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>Yes</option>
                <option>No</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Prior Claims (last 5 yrs) *</Form.Label>
            <ValidWrap valid={valid.priorClaims5y}>
              <Form.Control
                value={data.priorClaims5y}
                onChange={(e) => setData({ priorClaims5y: e.target.value })}
                placeholder="0"
                required
                className={
                  valid.priorClaims5y ? "is-valid" : data.priorClaims5y ? "is-invalid" : ""
                }
              />
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step4Coverage: React.FC<StepProps> = ({
  data,
  setData,
  helpEffect,
  helpKey,
  valid,
}) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[3].title}
        text={STEP_HELP[3].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Coverage A – Dwelling *</Form.Label>
            <ValidWrap valid={valid.coverageA_Dwelling}>
              <Form.Control
                value={data.coverageA_Dwelling}
                onChange={(e) => setData({ coverageA_Dwelling: e.target.value })}
                placeholder="e.g., 400000"
                required
                className={
                  valid.coverageA_Dwelling
                    ? "is-valid"
                    : data.coverageA_Dwelling
                    ? "is-invalid"
                    : ""
                }
              />
              <InputGroup.Text>$</InputGroup.Text>
            </ValidWrap>
            <FieldNote>Approximate rebuild cost; we’ll refine if needed.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Coverage C – Personal Property *</Form.Label>
            <ValidWrap valid={valid.coverageC_PersonalProperty}>
              <Form.Control
                value={data.coverageC_PersonalProperty}
                onChange={(e) => setData({ coverageC_PersonalProperty: e.target.value })}
                placeholder="e.g., 200000"
                required
                className={
                  valid.coverageC_PersonalProperty
                    ? "is-valid"
                    : data.coverageC_PersonalProperty
                    ? "is-invalid"
                    : ""
                }
              />
              <InputGroup.Text>$</InputGroup.Text>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Liability Limit *</Form.Label>
            <ValidWrap valid={valid.liabilityLimit}>
              <Form.Select
                value={data.liabilityLimit}
                onChange={(e) => setData({ liabilityLimit: e.target.value })}
                required
                className={
                  valid.liabilityLimit ? "is-valid" : data.liabilityLimit ? "is-invalid" : ""
                }
              >
                <option value="">Select…</option>
                <option>$100,000</option>
                <option>$300,000</option>
                <option>$500,000</option>
                <option>$1,000,000</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Medical Payments</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.medicalPayments}
                onChange={(e) => setData({ medicalPayments: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>$1,000</option>
                <option>$2,000</option>
                <option>$5,000</option>
                <option>$10,000</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Deductible *</Form.Label>
            <ValidWrap valid={valid.deductible}>
              <Form.Select
                value={data.deductible}
                onChange={(e) => setData({ deductible: e.target.value })}
                required
                className={valid.deductible ? "is-valid" : data.deductible ? "is-invalid" : ""}
              >
                <option value="">Select…</option>
                <option>$1,000</option>
                <option>$2,500</option>
                <option>$5,000</option>
                <option>1% Wind/Hail</option>
                <option>2% Wind/Hail</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Ordinance or Law</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.ordinanceOrLaw}
                onChange={(e) => setData({ ordinanceOrLaw: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>Yes</option>
                <option>No</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Replacement Cost on Contents</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.replacementCostContents}
                onChange={(e) => setData({ replacementCostContents: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>Yes</option>
                <option>No</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Water Backup</Form.Label>
            <ValidWrap valid={true}>
              <Form.Select
                value={data.waterBackup}
                onChange={(e) => setData({ waterBackup: e.target.value })}
              >
                <option value="">Select (optional)…</option>
                <option>Yes</option>
                <option>No</option>
              </Form.Select>
            </ValidWrap>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

/* ============================================================
   Summary (post-submit)
   ============================================================ */
const SummaryView: React.FC<{ data: WizardData }> = ({ data }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <h5 className="mb-3">Summary</h5>

      <Row className="g-3">
        <Col md={6}>
          <h6 className="text-muted">Contact</h6>
          <div><strong>Name:</strong> {data.fullName || "-"}</div>
          <div><strong>Email:</strong> {data.email || "-"}</div>
          <div><strong>Phone:</strong> {data.phone || "-"}</div>
          <div><strong>Address:</strong> {data.street || "-"}, {data.city || "-"}, {data.state || "-"} {data.zip || "-"}</div>
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Property</h6>
          <div><strong>Dwelling Type:</strong> {data.dwellingType || "-"}</div>
          <div><strong>Year Built:</strong> {data.yearBuilt || "-"}</div>
          <div><strong>Sq Ft / Stories:</strong> {data.squareFeet || "-"} / {data.stories || "-"}</div>
          <div><strong>Construction:</strong> {data.constructionType || "-"}</div>
          <div><strong>Roof:</strong> {data.roofType || "-"} ({data.roofYear || "-"})</div>
          <div><strong>Heating:</strong> {data.heatingType || "-"}</div>
        </Col>
      </Row>

      <hr />

      <Row className="g-3">
        <Col md={6}>
          <h6 className="text-muted">Occupancy & History</h6>
          <div><strong>Occupancy:</strong> {data.occupancy || "-"}</div>
          <div><strong>Months Occupied:</strong> {data.monthsOccupied || "-"}</div>
          <div><strong>Ownership:</strong> {data.ownershipStatus || "-"}</div>
          <div><strong>Mortgagee:</strong> {data.mortgageeName || "-"}</div>
          <div><strong>Monitored Alarm:</strong> {data.alarmMonitored || "-"}</div>
          <div><strong>Prior Claims (5y):</strong> {data.priorClaims5y || "-"}</div>
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Coverage</h6>
          <div><strong>Coverage A:</strong> {data.coverageA_Dwelling || "-"}</div>
          <div><strong>Coverage C:</strong> {data.coverageC_PersonalProperty || "-"}</div>
          <div><strong>Liability:</strong> {data.liabilityLimit || "-"}</div>
          <div><strong>MedPay:</strong> {data.medicalPayments || "-"}</div>
          <div><strong>Deductible:</strong> {data.deductible || "-"}</div>
          <div><strong>Ord/Law:</strong> {data.ordinanceOrLaw || "-"}</div>
          <div><strong>RC on Contents:</strong> {data.replacementCostContents || "-"}</div>
          <div><strong>Water Backup:</strong> {data.waterBackup || "-"}</div>
        </Col>
      </Row>

      <Alert variant="secondary" className="mt-3 mb-0">
        Thanks! We’ll review and follow up if anything else is needed.
      </Alert>
    </Card.Body>
  </Card>
);

/* ============================================================
   Main Component (autosave + themes)
   ============================================================ */
export const HOME_Insurance_Wizard_v06a_Themes: React.FC<{
  theme?: ThemeName;
  showThemeSwitcher?: boolean;
}> = ({ theme = "classic", showThemeSwitcher = false }) => {
  // activeIndex: 0..3 are data-entry slides, 4 is Summary
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [data, setData] = useState<WizardData>(defaultData);

  // Autosave state
  const [recordId, setRecordId] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedISO, setLastSavedISO] = useState<string | null>(null);

  // Live theme (can be controlled by prop or built-in switcher)
  const [themeState, setThemeState] = useState<ThemeName>(theme);
  useEffect(() => setThemeState(theme), [theme]);

  // Track which steps already animated their help text
  const prevIndexRef = useRef<number | null>(null);
  const helpPlayedRef = useRef<[boolean, boolean, boolean, boolean]>([
    false,
    false,
    false,
    false,
  ]);
  useEffect(() => {
    const prev = prevIndexRef.current;
    if (prev !== null && prev >= 0 && prev <= 3) {
      helpPlayedRef.current[prev] = true;
    }
    prevIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Shallow merge setter
  const patch = useCallback(
    (partial: Partial<WizardData>) => setData((prev) => ({ ...prev, ...partial })),
    []
  );

  // validity & errors
  const { valid, errors } = useMemo(() => buildValidity(data), [data]);

  // Required completion gate (for Finish -> Summary)
  const requiredOk =
    valid.fullName &&
    valid.email &&
    valid.phone &&
    valid.street &&
    valid.city &&
    valid.state &&
    valid.zip &&
    valid.dwellingType &&
    valid.yearBuilt &&
    valid.squareFeet &&
    valid.stories &&
    valid.constructionType &&
    valid.roofType &&
    valid.roofYear &&
    valid.heatingType &&
    valid.occupancy &&
    valid.monthsOccupied &&
    valid.ownershipStatus &&
    valid.mortgageeName &&
    valid.priorClaims5y &&
    valid.coverageA_Dwelling &&
    valid.coverageC_PersonalProperty &&
    valid.liabilityLimit &&
    valid.deductible;

  const atLastDataStep = activeIndex === 3;
  const atSummary = activeIndex === 4;

  const progress = useMemo(() => Math.round(((activeIndex + 1) / 5) * 100), [activeIndex]);

  // ---------- API helpers ----------
  type ApiWire = {
    mode: "create" | "update";
    recordId?: string | null;
    step: number; // 0-based step index when saved
    data: WizardData;
    meta: {
      testMode: boolean;
      version: string;
      savedAtISO: string;
    };
  };

  type ApiResponse = {
    ok: boolean;
    recordId: string;
    data?: Partial<WizardData>;
    error?: string;
  };

  const postToGateway = useCallback(
    async (mode: "create" | "update", stepIndex: number): Promise<ApiResponse> => {
      const wire: ApiWire = {
        mode,
        recordId,
        step: stepIndex,
        data,
        meta: {
          testMode: TEST_MODE,
          version: VERSION,
          savedAtISO: new Date().toISOString(),
        },
      };

      if (TEST_MODE) {
        await new Promise((r) => setTimeout(r, 450));
        const fakeId = recordId ?? `rec_${Math.random().toString(36).slice(2, 10)}`;
        const normalized: Partial<WizardData> = {
          ...data,
          fullName: data.fullName.trim(),
          city: data.city.trim(),
          state: data.state.trim().toUpperCase(),
        };
        return { ok: true, recordId: fakeId, data: normalized };
      }

      const res = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(wire),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        return {
          ok: false,
          recordId: recordId ?? "",
          error: `API error ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ""}`,
        };
      }

      const json = (await res.json().catch(() => ({}))) as Partial<ApiResponse>;
      if (!json || !json.recordId) {
        return { ok: false, recordId: recordId ?? "", error: "Malformed API response (missing recordId)" };
      }
      return { ok: true, recordId: json.recordId!, data: (json.data ?? {}) as Partial<WizardData> };
    },
    [API_GATEWAY_URL, TEST_MODE, VERSION, data, recordId]
  );

  const autosaveStep = useCallback(
    async (stepIndex: number) => {
      setSaving(true);
      setSaveError(null);
      const mode: "create" | "update" = recordId ? "update" : (stepIndex === 0 ? "create" : "create");
      const resp = await postToGateway(mode, stepIndex);
      setSaving(false);

      if (!resp.ok) {
        setSaveError(resp.error ?? "Failed to save. Please try again.");
        return false;
      }
      setRecordId(resp.recordId);
      if (resp.data) {
        setData((prev) => ({ ...prev, ...resp.data! }));
      }
      setLastSavedISO(new Date().toISOString());
      return true;
    },
    [postToGateway, recordId]
  );

  // ---------- Navigation handlers with autosave ----------
  const goNext = useCallback(async () => {
    if (atSummary) return;
    const saved = await autosaveStep(activeIndex);
    if (!saved) return;
    if (activeIndex < 3) setActiveIndex((i) => i + 1);
  }, [activeIndex, atSummary, autosaveStep]);

  const goBack = useCallback(() => {
    if (activeIndex > 0) setActiveIndex((i) => i - 1);
  }, [activeIndex]);

  const handleFinish = useCallback(async () => {
    if (!requiredOk) return;
    setSaving(true);
    setSaveError(null);
    try {
      const ok = await autosaveStep(3);
      if (!ok) return;
      setActiveIndex(4);
    } finally {
      setSaving(false);
    }
  }, [requiredOk, autosaveStep]);

  // Determine help effect for current slide (only first appearance)
  const effectiveHelp: HelpEffect =
    (activeIndex >=0 && activeIndex <= 3 && (helpPlayedRef.current as any)[activeIndex] === true)
      ? "none"
      : HELP_EFFECT;

  const stepCards = [
    <Step1Contact
      key="s1"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step2Property
      key="s2"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step3OccupancyHistory
      key="s3"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step4Coverage
      key="s4"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <SummaryView key="s5" data={data} />,
  ];

  // Right-side info panel visibility
  const showRightPanel = !atSummary;

  return (
    <div className={`hiw06-root theme-${themeState}`}>
      <Container className="py-4">
        {/* Header */}
        <Row className="mb-3 align-items-center">
          <Col lg={7}>
            <h3 className="mb-0">{LABELS.title}</h3>
            <div className="text-muted">{LABELS.subtitle}</div>
          </Col>
          <Col lg={5} className="text-lg-end mt-2 mt-lg-0 d-flex gap-2 align-items-center justify-content-lg-end">
            {showThemeSwitcher && (
              <Form.Select
                aria-label="Theme selector"
                value={themeState}
                onChange={(e) => setThemeState(e.target.value as ThemeName)}
                className="w-auto"
              >
                <option value="classic">Theme: Classic</option>
                <option value="midnight">Theme: Midnight</option>
                <option value="ocean">Theme: Ocean</option>
              </Form.Select>
            )}
            {recordId && (
              <Badge bg="light" text="dark">
                ID: {recordId}
              </Badge>
            )}
            {SHOW_VERSION && (
              <Badge bg="secondary" pill>
                {VERSION}
              </Badge>
            )}
          </Col>
        </Row>

        <Row>
          {/* Main form column */}
          <Col lg={9}>
            <Row className="mb-2">
              <Col>
                <div className="small text-muted d-flex align-items-center gap-2">
                  <span>{LABELS.notices.required}</span>
                  {saving && (
                    <span className="small text-muted d-inline-flex align-items-center">
                      <Spinner animation="border" size="sm" className="me-1" /> Saving…
                    </span>
                  )}
                  {!saving && lastSavedISO && (
                    <span className="small text-muted">Saved ✓ {new Date(lastSavedISO).toLocaleTimeString()}</span>
                  )}
                </div>
              </Col>
            </Row>

            {SHOW_PROGRESS_BAR && (
              <Row className="mb-3">
                <Col>
                  <ProgressBar now={progress} label={`${progress}%`} />
                </Col>
              </Row>
            )}

            {/* Step headline */}
            <Row className="mb-2">
              <Col>
                <h5 className="mb-0">{LABELS.stepTitles[activeIndex] ?? "Step"}</h5>
              </Col>
            </Row>

            {/* Slides */}
            <Carousel
              activeIndex={activeIndex}
              onSelect={() => {}}
              interval={undefined}
              controls={false}
              indicators={false}
              slide
              fade={false}
              className="mb-3"
            >
              {stepCards.map((card, i) => (
                <Carousel.Item key={`slide-${i}`} style={{ transitionDuration: `${450}ms` }}>
                  {card}
                </Carousel.Item>
              ))}
            </Carousel>

            {/* Errors */}
            {saveError && (
              <Alert variant="danger" className="mb-3">
                {saveError}
              </Alert>
            )}

            {/* Footer actions */}
            <Row className="g-2">
              <Col xs="auto">
                <Button
                  variant="outline-secondary"
                  onClick={goBack}
                  disabled={activeIndex === 0 || saving}
                >
                  {LABELS.buttons.back}
                </Button>
              </Col>

              {!atSummary && !atLastDataStep && (
                <Col xs="auto">
                  <Button variant="primary" onClick={goNext} disabled={saving}>
                    {saving ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Saving…
                      </>
                    ) : (
                      LABELS.buttons.next
                    )}
                  </Button>
                </Col>
              )}

              {!atSummary && atLastDataStep && (
                <Col xs="auto">
                  <Button
                    variant={requiredOk ? "success" : "secondary"}
                    onClick={handleFinish}
                    disabled={saving || !requiredOk}
                  >
                    {saving ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Saving…
                      </>
                    ) : (
                      LABELS.buttons.finish
                    )}
                  </Button>
                </Col>
              )}

              {atSummary && (
                <>
                  <Col xs="auto">
                    <Button variant="outline-primary" onClick={() => setActiveIndex(0)} disabled={saving}>
                      {LABELS.buttons.edit}
                    </Button>
                  </Col>
                  <Col xs="auto">
                    <Button variant="primary" disabled>
                      {LABELS.buttons.submit}
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </Col>

          {/* Right rail: persistent info panel */}
          <Col lg={3} className="mt-3 mt-lg-0">
            {!atSummary && (
              <>
                {!requiredOk && (
                  <>
                    <HelpPanel
                      title="Heads up"
                      text="A few items still need attention before we can finish."
                      effect="none"
                    />
                    <Alert variant="info" className="mb-3">
                      <div className="fw-semibold mb-1">What to fix</div>
                      <div className="small text-muted">Please complete or correct the following:</div>
                      <ul className="mt-2 mb-0 ps-3 small">
                        {errors.map((e, i) => (
                          <li key={i}>
                            <strong>{e.label}</strong> — {e.reason}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  </>
                )}
              </>
            )}
          </Col>
        </Row>

        {/* Styles - THEME VARIABLES & OVERRIDES */}
        <style>{`
          .tw-cursor {
            display: inline-block;
            margin-left: 1px;
            animation: tw-blink 1s step-end infinite;
          }
          @keyframes tw-blink {
            0%, 50% { opacity: 1; }
            50.01%, 100% { opacity: 0; }
          }

          /* Root defaults (will be overridden by theme classes) */
          .hiw06-root {
            --bg: #ffffff;
            --text: #212529;
            --muted: #6c757d;
            --border: #dee2e6;
            --card-bg: #ffffff;
            --primary: #0d6efd;
            --secondary: #6c757d;
            --success: #198754;
            --info: #0dcaf0;
            --danger: #dc3545;
            --progress-bg: #e9ecef;
            --progress-bar: var(--primary);
            --alert-info-bg: #e7f5ff;
            --alert-info-border: #b3e0ff;
            --alert-danger-bg: #fde2e1;
            --alert-danger-border: #f5b5b2;
            --input-bg: #ffffff;
            --input-text: var(--text);
            --badge-secondary-text: #ffffff;
          }

          /* Theme: Classic (Bootstrap-like) */
          .hiw06-root.theme-classic { }
          /* Theme: Midnight (dark) */
          .hiw06-root.theme-midnight {
            --bg: #0f172a; /* slate-900 */
            --text: #e2e8f0; /* slate-200 */
            --muted: #94a3b8; /* slate-400 */
            --border: #1e293b; /* slate-800 */
            --card-bg: #111827; /* gray-900 */
            --primary: #22d3ee; /* cyan-400 */
            --secondary: #64748b; /* slate-500 */
            --success: #34d399; /* emerald-400 */
            --info: #38bdf8; /* sky-400 */
            --danger: #f87171; /* red-400 */
            --progress-bg: #0b1220;
            --progress-bar: var(--primary);
            --alert-info-bg: #0b2430;
            --alert-info-border: #113646;
            --alert-danger-bg: #2a1515;
            --alert-danger-border: #4a1e1e;
            --input-bg: #0b1220;
            --input-text: var(--text);
            --badge-secondary-text: #0f172a;
          }
          /* Theme: Ocean (light, teal/indigo) */
          .hiw06-root.theme-ocean {
            --bg: #f0fbff;
            --text: #0b2545;
            --muted: #406882;
            --border: #cfe8f3;
            --card-bg: #ffffff;
            --primary: #006d77;
            --secondary: #5c6b8a;
            --success: #2a9d8f;
            --info: #00b4d8;
            --danger: #ef476f;
            --progress-bg: #e6f7ff;
            --progress-bar: #00a1c6;
            --alert-info-bg: #e6faff;
            --alert-info-border: #bfefff;
            --alert-danger-bg: #ffe8ef;
            --alert-danger-border: #ffc3d4;
            --input-bg: #ffffff;
            --input-text: var(--text);
            --badge-secondary-text: #ffffff;
          }

          /* Apply theme variables */
          .hiw06-root {
            background-color: var(--bg);
            color: var(--text);
            min-height: 100%;
          }
          .hiw06-root .text-muted { color: var(--muted) !important; }

          /* Cards & borders */
          .hiw06-root .card {
            background-color: var(--card-bg);
            border-color: var(--border);
          }
          .hiw06-root .alert-info {
            background-color: var(--alert-info-bg);
            border-color: var(--alert-info-border);
            color: var(--text);
          }
          .hiw06-root .alert-danger {
            background-color: var(--alert-danger-bg);
            border-color: var(--alert-danger-border);
            color: var(--text);
          }

          /* Buttons */
          .hiw06-root .btn-primary {
            background-color: var(--primary);
            border-color: var(--primary);
            color: #ffffff;
          }
          .hiw06-root .btn-success {
            background-color: var(--success);
            border-color: var(--success);
            color: #ffffff;
          }
          .hiw06-root .btn-outline-secondary {
            color: var(--secondary);
            border-color: var(--secondary);
          }
          .hiw06-root .btn-outline-primary {
            color: var(--primary);
            border-color: var(--primary);
          }
          .hiw06-root .btn-primary:disabled,
          .hiw06-root .btn-success:disabled {
            opacity: 0.75;
          }

          /* Badges */
          .hiw06-root .badge.bg-secondary {
            background-color: var(--secondary) !important;
            color: var(--badge-secondary-text) !important;
          }
          .hiw06-root .badge.bg-light {
            background-color: rgba(0,0,0,0.06) !important;
            color: var(--text) !important;
            border: 1px solid var(--border);
          }

          /* Inputs */
          .hiw06-root .form-control,
          .hiw06-root .form-select,
          .hiw06-root .input-group-text {
            background-color: var(--input-bg);
            color: var(--input-text);
            border-color: var(--border);
          }
          .hiw06-root .form-control:focus,
          .hiw06-root .form-select:focus {
            border-color: var(--primary);
            box-shadow: 0 0 0 .2rem rgba(13, 110, 253, 0.25);
          }
          .hiw06-root .is-valid { border-color: var(--success) !important; }
          .hiw06-root .is-invalid { border-color: var(--danger) !important; }

          /* Progress */
          .hiw06-root .progress { background-color: var(--progress-bg); }
          .hiw06-root .progress-bar { background-color: var(--progress-bar); }

          /* Carousel item width fix */
          .hiw06-root .carousel-item > .card { width: 100%; }
        `}</style>
      </Container>
    </div>
  );
};

// Default export for convenience
export default HOME_Insurance_Wizard_v06a_Themes;
